import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface SharePayload {
  email: string;
  expirationDate: string;
}

export interface UnsharePayload {
  userId: number;
}

@Injectable({
  providedIn: 'root'
})
export class FilesService {

  constructor(private httpClient: HttpClient) { }

  /**
   * Uploads a file to the backend to be stored in the Files table.
   * @param ownerUserId - The ID of the user who owns the file.
   * @param file - The file object to upload.
   * @param description - (Optional) A description for the file.
   * @param expirationDate - (Optional) The expiration date for the file.
   * @returns An Observable containing the server's response.
   */
  uploadFile(ownerUserId: number, file: File, description?: string, expirationDate?: Date): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('ownerUserId', ownerUserId.toString());
    formData.append('file', file, file.name);
    if (description) {
      formData.append('description', description);
    }
    if (expirationDate) {
      formData.append('expirationDate', expirationDate.toISOString());
    }

    return this.httpClient.post(`${environment.dataServicesURL}files/upload`, formData, { withCredentials: true });
  }

  /**
   * Retrieves all files uploaded by a specific user.
   * @param ownerUserId - The ID of the user whose files should be loaded.
   * @returns An Observable with the list of files.
   */
  getUserFiles(ownerUserId: number): Observable<any> {
    return this.httpClient.get(`${environment.dataServicesURL}files/user/${ownerUserId}`, { withCredentials: true });
  }

  /**
   * Deletes a file from the backend.
   * @param fileId - The ID of the file to delete.
   * @returns An Observable with the server's response.
   */
  deleteFile(fileId: number): Observable<any> {
    return this.httpClient.delete(`${environment.dataServicesURL}files/${fileId}`, { withCredentials: true });
  }

  /**
   * Downloads a file from the backend.
   * @param fileId - The ID of the file to download.
   * @returns An Observable containing the file as a Blob.
   */
  downloadFile(fileId: number): Observable<Blob> {
    return this.httpClient.get(
      `${environment.dataServicesURL}files/download/${fileId}`,
      { responseType: 'blob', withCredentials: true }
    );
  }

  /**
   * Shares a file with a user.
   * @param fileId The ID of the file to share.
   * @param email The email address of the user to share the file with.
   * @param expirationDate The expiration date for the share.
   */
  shareFile(fileId: number, email: string, expirationDate: Date): Observable<any> {
    const payload: SharePayload = {
      email,
      expirationDate: expirationDate.toISOString()
    };
    return this.httpClient.post(
      `${environment.dataServicesURL}files/${fileId}/share`,
      payload,
      { withCredentials: true }
    );
  }

  /**
   * Revokes a file share from a user.
   * @param fileId The ID of the file to unshare.
   * @param userId The ID of the user to revoke the share from.
   */
  unshareFile(fileId: number, userId: number): Observable<any> {
    const payload: UnsharePayload = { userId };
    return this.httpClient.post(
      `${environment.dataServicesURL}files/${fileId}/unshare`,
      payload,
      { withCredentials: true }
    );
  }
}
